/**
 * propMeshes – the themed pieces around the lesson desk, built in code.
 *
 * roomTheme.ts decides which pieces a lesson gets and where they stand; this
 * builds them. Everything is procedural (lathe profiles for pottery and glass,
 * displaced solids for rocks, canvas textures for paper, maps and boards), so
 * there is nothing to download and nothing to license.
 *
 * Budget, because this runs on phones and Quest headsets:
 *   - repeated parts (books on a shelf, abacus beads, leaves, DNA spheres) are
 *     one InstancedMesh each, not one mesh per part;
 *   - materials and textures are shared and cached; only geometry is per piece,
 *     and `disposeRoomProps` frees it on every rebuild;
 *   - every piece stands on its own soft contact shadow, which grounds it on
 *     any skybox without the cost of a shadow map.
 *
 * Every builder returns a group whose origin is on the floor (or table top)
 * at the piece's centre, facing +Z (towards the viewer once yawed).
 */

import * as THREE from 'three';

import { buildDesk, type DeskFinish } from './deskMesh';
import {
  SIDE_TABLE,
  seededRandom,
  type EaselBoard,
  type FloorKind,
  type GradeBand,
  type PlacedProp,
  type RoomPlan,
  type SmallKind,
} from './roomTheme';

// ---------------------------------------------------------------------------
// Shared materials and textures
// ---------------------------------------------------------------------------

const materialCache = new Map<string, THREE.Material>();
const textureCache = new Map<string, THREE.Texture | null>();

function mat(color: number, roughness = 0.6, metalness = 0, extra: Partial<THREE.MeshStandardMaterialParameters> = {}) {
  const key = `${color}|${roughness}|${metalness}|${JSON.stringify(extra, (_k, v) => (v instanceof THREE.Texture ? v.uuid : v))}`;
  let m = materialCache.get(key);
  if (!m) {
    m = new THREE.MeshStandardMaterial({ color, roughness, metalness, ...extra });
    materialCache.set(key, m);
  }
  return m as THREE.MeshStandardMaterial;
}

function glass(tint = 0xdff3ff) {
  return mat(tint, 0.08, 0, { transparent: true, opacity: 0.32, depthWrite: false });
}

function glow(color: number) {
  return mat(color, 0.4, 0, { emissive: color, emissiveIntensity: 1.2 });
}

function canvasTex(key: string, w: number, h: number, draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void) {
  if (textureCache.has(key)) return textureCache.get(key)!;
  let tex: THREE.Texture | null = null;
  if (typeof document !== 'undefined') {
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      draw(ctx, w, h);
      tex = new THREE.CanvasTexture(canvas);
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.anisotropy = 4;
    }
  }
  textureCache.set(key, tex);
  return tex;
}


// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

type Mat = THREE.Material;

function add(parent: THREE.Object3D, geometry: THREE.BufferGeometry, material: Mat | Mat[], x = 0, y = 0, z = 0) {
  const m = new THREE.Mesh(geometry, material);
  m.position.set(x, y, z);
  parent.add(m);
  return m;
}

const box = (w: number, h: number, d: number) => new THREE.BoxGeometry(w, h, d);
const cyl = (rt: number, rb: number, h: number, seg = 20) => new THREE.CylinderGeometry(rt, rb, h, seg);
const sph = (r: number, w = 14, h = 10) => new THREE.SphereGeometry(r, w, h);

/** A turned shape from (radius, height) pairs, bottom to top. */
function lathe(profile: [number, number][], segments = 24) {
  return new THREE.LatheGeometry(
    profile.map(([r, y]) => new THREE.Vector2(r, y)),
    segments
  );
}

/** A soft round shadow on the floor, shared texture. */
function contactShadow(radius: number) {
  const map = canvasTex('contact-shadow', 128, 128, (ctx, w, h) => {
    const g = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w / 2);
    g.addColorStop(0, 'rgba(0,0,0,0.55)');
    g.addColorStop(0.6, 'rgba(0,0,0,0.25)');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
  });
  const m = new THREE.Mesh(
    new THREE.PlaneGeometry(radius * 2.4, radius * 2.4),
    map
      ? mat(0xffffff, 1, 0, { map, transparent: true, depthWrite: false })
      : mat(0x000000, 1, 0, { transparent: true, opacity: 0.25, depthWrite: false })
  );
  m.rotation.x = -Math.PI / 2;
  m.position.y = 0.003;
  m.renderOrder = -1;
  m.userData.contactShadow = true;
  return m;
}

// ---------------------------------------------------------------------------
// Canvas art
// ---------------------------------------------------------------------------

function globeTexture() {
  return canvasTex('globe', 512, 256, (ctx, w, h) => {
    const rand = seededRandom(21);
    ctx.fillStyle = '#2d6fa8';
    ctx.fillRect(0, 0, w, h);
    // Land: clusters of overlapping blobs, so the coastlines read as continents.
    const land = ['#6f9a4a', '#8aa65a', '#b59a64', '#5d8a3e'];
    for (let c = 0; c < 7; c += 1) {
      const cx = rand() * w;
      const cy = h * (0.2 + rand() * 0.6);
      for (let i = 0; i < 26; i += 1) {
        ctx.fillStyle = land[Math.floor(rand() * land.length)];
        ctx.beginPath();
        ctx.ellipse(cx + (rand() - 0.5) * 90, cy + (rand() - 0.5) * 60, 8 + rand() * 22, 6 + rand() * 14, rand() * 3, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    // Ice caps and graticule.
    ctx.fillStyle = '#eef3f6';
    ctx.fillRect(0, 0, w, 12);
    ctx.fillRect(0, h - 12, w, 12);
    ctx.strokeStyle = 'rgba(255,255,255,0.25)';
    ctx.lineWidth = 1;
    for (let x = 0; x < w; x += w / 12) {
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke();
    }
    for (let y = h / 6; y < h; y += h / 6) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke();
    }
  });
}

function parchment(key: string, draw?: (ctx: CanvasRenderingContext2D, w: number, h: number) => void) {
  return canvasTex(key, 512, 512, (ctx, w, h) => {
    const rand = seededRandom(5);
    ctx.fillStyle = '#e8d6aa';
    ctx.fillRect(0, 0, w, h);
    for (let i = 0; i < 400; i += 1) {
      ctx.fillStyle = `rgba(150,110,60,${rand() * 0.06})`;
      ctx.beginPath();
      ctx.arc(rand() * w, rand() * h, 4 + rand() * 30, 0, Math.PI * 2);
      ctx.fill();
    }
    const g = ctx.createRadialGradient(w / 2, h / 2, w * 0.3, w / 2, h / 2, w * 0.72);
    g.addColorStop(0, 'rgba(0,0,0,0)');
    g.addColorStop(1, 'rgba(90,55,20,0.45)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
    draw?.(ctx, w, h);
  });
}

function boardTexture(kind: EaselBoard) {
  switch (kind) {
    case 'map':
      return parchment('board-map', (ctx, w, h) => {
        const rand = seededRandom(31);
        ctx.strokeStyle = 'rgba(80,50,20,0.8)';
        ctx.lineWidth = 3;
        for (let c = 0; c < 3; c += 1) {
          ctx.beginPath();
          const cx = w * (0.25 + rand() * 0.5);
          const cy = h * (0.25 + rand() * 0.5);
          for (let a = 0; a <= Math.PI * 2 + 0.01; a += 0.2) {
            const r = 60 + rand() * 50;
            const x = cx + Math.cos(a) * r;
            const y = cy + Math.sin(a) * r * 0.7;
            if (a === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          }
          ctx.closePath();
          ctx.stroke();
        }
        // Compass rose.
        ctx.fillStyle = 'rgba(120,40,30,0.8)';
        ctx.beginPath();
        ctx.moveTo(w - 70, h - 120); ctx.lineTo(w - 58, h - 70); ctx.lineTo(w - 82, h - 70); ctx.closePath();
        ctx.fill();
        ctx.font = 'bold 28px serif';
        ctx.fillText('N', w - 80, h - 128);
      });
    case 'letters':
      return canvasTex('board-letters', 512, 512, (ctx, w, h) => {
        ctx.fillStyle = '#2f4f3a';
        ctx.fillRect(0, 0, w, h);
        ctx.fillStyle = '#f4f1e6';
        ctx.font = '600 76px serif';
        ctx.textAlign = 'center';
        ctx.fillText('Aa  Bb  Cc', w / 2, h * 0.3);
        ctx.font = '600 80px sans-serif';
        ctx.fillText('अ  आ  इ', w / 2, h * 0.58);
        ctx.font = 'italic 44px serif';
        ctx.fillText('Read · Write · Speak', w / 2, h * 0.82);
      });
    case 'periodic':
      return canvasTex('board-periodic', 512, 384, (ctx, w, h) => {
        ctx.fillStyle = '#f7f7f2';
        ctx.fillRect(0, 0, w, h);
        const cols = 18;
        const rows = 7;
        const cw = (w - 20) / cols;
        const ch = (h - 60) / rows;
        const colours = ['#f6b26b', '#ffd966', '#93c47d', '#6fa8dc', '#b4a7d6', '#e06666', '#76a5af'];
        for (let r = 0; r < rows; r += 1) {
          for (let c = 0; c < cols; c += 1) {
            const present = r === 0 ? c === 0 || c === 17 : r < 3 ? c < 2 || c > 11 : true;
            if (!present) continue;
            ctx.fillStyle = colours[(c < 2 ? 0 : c > 11 ? 3 + (c % 3) : 2) % colours.length];
            ctx.fillRect(10 + c * cw + 1, 40 + r * ch + 1, cw - 2, ch - 2);
          }
        }
        ctx.fillStyle = '#333';
        ctx.font = 'bold 22px sans-serif';
        ctx.fillText('Periodic Table', 14, 28);
      });
    case 'graph':
      return canvasTex('board-graph', 512, 512, (ctx, w, h) => {
        ctx.fillStyle = '#fbfbf7';
        ctx.fillRect(0, 0, w, h);
        ctx.strokeStyle = 'rgba(80,140,200,0.25)';
        ctx.lineWidth = 1;
        for (let i = 0; i <= w; i += 32) {
          ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i, h); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(0, i); ctx.lineTo(w, i); ctx.stroke();
        }
        ctx.strokeStyle = '#222';
        ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(40, h - 40); ctx.lineTo(w - 20, h - 40); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(40, h - 40); ctx.lineTo(40, 20); ctx.stroke();
        ctx.strokeStyle = '#d0453a';
        ctx.lineWidth = 4;
        ctx.beginPath();
        for (let x = 0; x <= w - 60; x += 4) {
          const y = h - 40 - (Math.sin(x / 50) * 90 + x * 0.5);
          if (x === 0) ctx.moveTo(40 + x, y);
          else ctx.lineTo(40 + x, y);
        }
        ctx.stroke();
      });
    case 'timeline':
      return parchment('board-timeline', (ctx, w, h) => {
        ctx.strokeStyle = '#4a2e1a';
        ctx.fillStyle = '#4a2e1a';
        ctx.lineWidth = 5;
        ctx.beginPath(); ctx.moveTo(40, h / 2); ctx.lineTo(w - 40, h / 2); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(w - 40, h / 2); ctx.lineTo(w - 60, h / 2 - 12); ctx.lineTo(w - 60, h / 2 + 12); ctx.fill();
        ctx.font = 'bold 22px serif';
        ctx.textAlign = 'center';
        ['3000 BC', '500 BC', 'AD 1', 'AD 1500', 'Today'].forEach((label, i) => {
          const x = 70 + i * ((w - 150) / 4);
          ctx.beginPath(); ctx.arc(x, h / 2, 10, 0, Math.PI * 2); ctx.fill();
          ctx.fillText(label, x, i % 2 ? h / 2 + 50 : h / 2 - 30);
        });
        ctx.font = 'bold 34px serif';
        ctx.fillText('Timeline', w / 2, 70);
      });
    case 'poem':
      return canvasTex('board-poem', 512, 512, (ctx, w, h) => {
        const rand = seededRandom(41);
        ctx.fillStyle = '#fdfcf6';
        ctx.fillRect(0, 0, w, h);
        ctx.strokeStyle = 'rgba(90,140,200,0.35)';
        ctx.lineWidth = 2;
        for (let y = 110; y < h - 20; y += 36) {
          ctx.beginPath(); ctx.moveTo(30, y); ctx.lineTo(w - 30, y); ctx.stroke();
        }
        ctx.strokeStyle = 'rgba(220,80,80,0.5)';
        ctx.beginPath(); ctx.moveTo(70, 0); ctx.lineTo(70, h); ctx.stroke();
        ctx.fillStyle = '#23324a';
        ctx.font = 'italic bold 40px serif';
        ctx.fillText('My Poem', 90, 72);
        // Handwriting, as scribbled word shapes rather than real text.
        ctx.strokeStyle = '#23324a';
        ctx.lineWidth = 2.5;
        for (let y = 104; y < h - 40; y += 36) {
          let x = 84;
          const end = w - 60 - rand() * 140;
          while (x < end) {
            const len = 20 + rand() * 50;
            ctx.beginPath();
            for (let t = 0; t <= len; t += 3) {
              const yy = y - 8 + Math.sin((x + t) * 0.6) * 5;
              if (t === 0) ctx.moveTo(x + t, yy);
              else ctx.lineTo(x + t, yy);
            }
            ctx.stroke();
            x += len + 12;
          }
        }
      });
    case 'cell':
      return canvasTex('board-cell', 512, 384, (ctx, w, h) => {
        ctx.fillStyle = '#f7f7f2';
        ctx.fillRect(0, 0, w, h);
        ctx.fillStyle = '#cfe8c4';
        ctx.strokeStyle = '#3d7a33';
        ctx.lineWidth = 6;
        ctx.beginPath(); ctx.ellipse(w / 2, h / 2 + 10, 200, 140, 0, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
        ctx.fillStyle = '#b48ad8';
        ctx.beginPath(); ctx.arc(w / 2 + 30, h / 2 + 10, 45, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#6b3fa0';
        ctx.beginPath(); ctx.arc(w / 2 + 38, h / 2 + 4, 14, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#f2a541';
        for (const [x, y] of [[170, 150], [330, 260], [180, 270]]) {
          ctx.beginPath(); ctx.ellipse(x, y, 28, 13, 0.5, 0, Math.PI * 2); ctx.fill();
        }
        ctx.fillStyle = '#333';
        ctx.font = 'bold 26px sans-serif';
        ctx.fillText('The Cell', 20, 36);
      });
    case 'circuit':
      return canvasTex('board-circuit', 512, 384, (ctx, w, h) => {
        ctx.fillStyle = '#26382d';
        ctx.fillRect(0, 0, w, h);
        ctx.strokeStyle = '#f0f0e6';
        ctx.lineWidth = 4;
        ctx.strokeRect(90, 90, 330, 200);
        // Battery
        ctx.fillStyle = '#26382d';
        ctx.fillRect(80, 160, 20, 60);
        ctx.beginPath(); ctx.moveTo(70, 175); ctx.lineTo(110, 175); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(80, 205); ctx.lineTo(100, 205); ctx.stroke();
        // Resistor zigzag
        ctx.fillRect(210, 80, 90, 20);
        ctx.beginPath(); ctx.moveTo(210, 90);
        for (let i = 0; i < 6; i += 1) ctx.lineTo(217 + i * 14, i % 2 ? 78 : 102);
        ctx.lineTo(300, 90); ctx.stroke();
        // Bulb
        ctx.fillRect(400, 160, 40, 60);
        ctx.beginPath(); ctx.arc(420, 190, 24, 0, Math.PI * 2); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(403, 173); ctx.lineTo(437, 207); ctx.moveTo(437, 173); ctx.lineTo(403, 207); ctx.stroke();
        ctx.fillStyle = '#f0f0e6';
        ctx.font = '28px sans-serif';
        ctx.fillText('V = I × R', 190, 350);
      });
    case 'geometry':
      return canvasTex('board-geometry', 512, 384, (ctx, w, h) => {
        ctx.fillStyle = '#fbfbf7';
        ctx.fillRect(0, 0, w, h);
        ctx.strokeStyle = '#1f4e8c';
        ctx.lineWidth = 4;
        ctx.beginPath(); ctx.moveTo(60, 320); ctx.lineTo(260, 320); ctx.lineTo(60, 120); ctx.closePath(); ctx.stroke();
        ctx.strokeRect(60, 295, 25, 25);
        ctx.strokeStyle = '#c0392b';
        ctx.beginPath(); ctx.arc(390, 200, 90, 0, Math.PI * 2); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(390, 200); ctx.lineTo(480, 200); ctx.stroke();
        ctx.fillStyle = '#222';
        ctx.font = 'italic 28px serif';
        ctx.fillText('a² + b² = c²', 70, 60);
        ctx.fillText('r', 430, 190);
        ctx.fillText('A = πr²', 340, 340);
      });
    case 'code':
      return canvasTex('board-code', 512, 384, (ctx, w, h) => {
        const rand = seededRandom(19);
        ctx.fillStyle = '#0f1720';
        ctx.fillRect(0, 0, w, h);
        const colours = ['#7ee787', '#79c0ff', '#ffa657', '#d2a8ff', '#c9d1d9'];
        for (let y = 30; y < h - 16; y += 24) {
          let x = 24 + Math.floor(rand() * 4) * 24;
          while (x < w - 40 && rand() > 0.18) {
            const len = 24 + rand() * 80;
            ctx.fillStyle = colours[Math.floor(rand() * colours.length)];
            ctx.fillRect(x, y, len, 10);
            x += len + 12;
          }
        }
      });
    case 'chalk':
    default:
      return canvasTex('board-chalk', 512, 384, (ctx, w, h) => {
        ctx.fillStyle = '#26382d';
        ctx.fillRect(0, 0, w, h);
        ctx.strokeStyle = 'rgba(240,240,230,0.85)';
        ctx.fillStyle = 'rgba(240,240,230,0.85)';
        ctx.lineWidth = 3;
        // A leaf and a sun: primary science, drawn in chalk.
        ctx.beginPath(); ctx.ellipse(150, 200, 90, 45, -0.5, 0, Math.PI * 2); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(80, 245); ctx.lineTo(225, 150); ctx.stroke();
        ctx.beginPath(); ctx.arc(380, 120, 45, 0, Math.PI * 2); ctx.stroke();
        for (let a = 0; a < Math.PI * 2; a += Math.PI / 6) {
          ctx.beginPath();
          ctx.moveTo(380 + Math.cos(a) * 58, 120 + Math.sin(a) * 58);
          ctx.lineTo(380 + Math.cos(a) * 78, 120 + Math.sin(a) * 78);
          ctx.stroke();
        }
        ctx.font = '32px sans-serif';
        ctx.fillText('Observe · Ask · Explore', 90, 340);
      });
  }
}

function faceTexture(char: string, bg: string) {
  return canvasTex(`face-${char}-${bg}`, 128, 128, (ctx, w, h) => {
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = 'rgba(255,255,255,0.6)';
    ctx.lineWidth = 8;
    ctx.strokeRect(6, 6, w - 12, h - 12);
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 84px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(char, w / 2, h / 2 + 4);
  });
}

// ---------------------------------------------------------------------------
// Floor pieces
// ---------------------------------------------------------------------------

type Rand = () => number;

/** A rock: an icosahedron with its corners pushed in and out, flat shaded. */
function rockGeometry(r: number, rand: Rand) {
  const g = new THREE.IcosahedronGeometry(r, 1);
  const pos = g.getAttribute('position');
  const offsets = new Map<string, number>();
  for (let i = 0; i < pos.count; i += 1) {
    const key = `${pos.getX(i).toFixed(4)},${pos.getY(i).toFixed(4)},${pos.getZ(i).toFixed(4)}`;
    if (!offsets.has(key)) offsets.set(key, 0.72 + rand() * 0.5);
    const k = offsets.get(key)!;
    pos.setXYZ(i, pos.getX(i) * k, pos.getY(i) * k * 0.7, pos.getZ(i) * k);
  }
  g.computeVertexNormals();
  return g;
}

const ROCK_COLOURS = [0x7d776f, 0x8b7d6b, 0x5f5a55, 0x9a8f80, 0x6b5d50, 0xa38d74];

function buildRocks(rand: Rand) {
  const g = new THREE.Group();
  const count = 3 + Math.floor(rand() * 3);
  for (let i = 0; i < count; i += 1) {
    const r = 0.07 + rand() * (i === 0 ? 0.13 : 0.08);
    const m = add(g, rockGeometry(r, rand), mat(ROCK_COLOURS[Math.floor(rand() * ROCK_COLOURS.length)], 0.9, 0, { flatShading: true }));
    const a = rand() * Math.PI * 2;
    const d = i === 0 ? 0 : 0.12 + rand() * 0.14;
    m.rotation.set(rand() * 0.6, rand() * Math.PI * 2, rand() * 0.6);
    m.position.set(Math.cos(a) * d, 0, Math.sin(a) * d);
    // Each rock rests on the floor by its own lowest point.
    m.updateMatrixWorld(true);
    m.position.y = -new THREE.Box3().setFromObject(m).min.y;
  }
  return g;
}

function buildGlobe() {
  const g = new THREE.Group();
  const wood = mat(0x5a3a22, 0.6);
  const brass = mat(0xb08d3c, 0.35, 0.8);
  add(g, cyl(0.2, 0.24, 0.05, 28), wood, 0, 0.025, 0);
  add(g, cyl(0.025, 0.035, 0.6, 12), wood, 0, 0.35, 0);
  const holder = new THREE.Group();
  holder.position.y = 0.87;
  holder.rotation.z = THREE.MathUtils.degToRad(23.4);
  const map = globeTexture();
  add(holder, sph(0.22, 32, 20), map ? mat(0xffffff, 0.45, 0, { map }) : mat(0x2d6fa8, 0.45));
  const ring = add(holder, new THREE.TorusGeometry(0.245, 0.008, 6, 40, Math.PI), brass);
  ring.rotation.set(0, Math.PI / 2, Math.PI / 2);
  g.add(holder);
  return g;
}

function buildStrata(rand: Rand) {
  const g = new THREE.Group();
  const layers = [0xc9a36b, 0x8f6f4f, 0xd8c7a3, 0x6e5a4a, 0xb88c5a, 0x9c9489, 0xcdb38a];
  let y = 0;
  add(g, cyl(0.2, 0.2, 0.04, 24), mat(0x333333, 0.8), 0, 0.02, 0);
  y = 0.04;
  const n = 6 + Math.floor(rand() * 2);
  for (let i = 0; i < n; i += 1) {
    const h = 0.07 + rand() * 0.07;
    add(g, cyl(0.16 + rand() * 0.01, 0.165, h, 24), mat(layers[i % layers.length], 0.95), 0, y + h / 2, 0);
    y += h;
  }
  // Topsoil and grass.
  add(g, cyl(0.165, 0.165, 0.04, 24), mat(0x4b3621, 1), 0, y + 0.02, 0);
  add(g, cyl(0.165, 0.165, 0.015, 24), mat(0x5c8a3a, 0.9), 0, y + 0.047, 0);
  return g;
}

const POT_PROFILES: [number, number][][] = [
  // Amphora
  [[0, 0], [0.05, 0], [0.07, 0.04], [0.16, 0.2], [0.18, 0.32], [0.15, 0.46], [0.07, 0.55], [0.055, 0.62], [0.08, 0.66], [0.07, 0.68], [0, 0.68]],
  // Wide jar
  [[0, 0], [0.09, 0], [0.16, 0.08], [0.19, 0.2], [0.16, 0.3], [0.11, 0.34], [0.12, 0.37], [0, 0.37]],
  // Bowl
  [[0, 0], [0.06, 0], [0.14, 0.05], [0.19, 0.12], [0.2, 0.14], [0, 0.14]],
];

function buildAmphora(rand: Rand) {
  const g = new THREE.Group();
  const terracotta = [0xb5653e, 0xa65a35, 0xc2774a][Math.floor(rand() * 3)];
  const count = 1 + Math.floor(rand() * 3);
  for (let i = 0; i < count; i += 1) {
    const profile = POT_PROFILES[i === 0 ? 0 : 1 + Math.floor(rand() * 2)];
    const s = i === 0 ? 1 : 0.55 + rand() * 0.3;
    const pot = add(g, lathe(profile.map(([r, y]) => [r * s, y * s] as [number, number]), 28), mat(terracotta, 0.85));
    if (i > 0) {
      const a = rand() * Math.PI * 2;
      pot.position.set(Math.cos(a) * 0.24, 0, Math.sin(a) * 0.24);
    } else {
      // A painted band, the way Greek and Indus pottery was decorated.
      const band = add(g, cyl(0.176, 0.178, 0.035, 28, ), mat(0x2a1a12, 0.8), 0, 0.3, 0);
      band.scale.set(1.0, 1, 1.0);
      for (const side of [-1, 1]) {
        const handle = add(g, new THREE.TorusGeometry(0.06, 0.012, 6, 14, Math.PI), mat(terracotta, 0.85), side * 0.13, 0.55, 0);
        handle.rotation.set(0, side > 0 ? 0 : Math.PI, -Math.PI / 2);
      }
    }
  }
  return g;
}

function buildColumn(rand: Rand) {
  const g = new THREE.Group();
  const marble = mat(0xe9e4da, 0.55);
  add(g, box(0.5, 0.1, 0.5), marble, 0, 0.05, 0);
  add(g, cyl(0.2, 0.22, 0.08, 24), marble, 0, 0.14, 0);
  const h = 0.8 + rand() * 0.3;
  const shaft = cyl(0.16, 0.17, h, 32, );
  // Fluting: push the surface in along 16 grooves.
  const pos = shaft.getAttribute('position');
  for (let i = 0; i < pos.count; i += 1) {
    const x = pos.getX(i);
    const z = pos.getZ(i);
    const r = Math.hypot(x, z);
    if (r < 0.1) continue;
    const a = Math.atan2(z, x);
    const k = 1 - 0.06 * Math.max(0, Math.cos(a * 16));
    pos.setX(i, x * k);
    pos.setZ(i, z * k);
  }
  shaft.computeVertexNormals();
  add(g, shaft, marble, 0, 0.18 + h / 2, 0);
  // A broken chunk lying beside it.
  const chunk = add(g, cyl(0.15, 0.16, 0.22, 24), marble, 0.3, 0.15, 0.12);
  chunk.rotation.set(Math.PI / 2, 0, 0.4);
  return g;
}

const BOOK_COLOURS = [0x8b1e1e, 0x1f3f6e, 0x2f5d3a, 0x6b4a2b, 0x5a2d6b, 0xc49a3a, 0x2b2b2b, 0x9c4a2a, 0x3a6b6b];

/** Books standing on a shelf of width `w`, as one InstancedMesh. */
function booksRow(w: number, depth: number, rand: Rand, maxH: number) {
  const geo = box(1, 1, 1);
  const count = Math.floor(w / 0.035);
  const mesh = new THREE.InstancedMesh(geo, mat(0xffffff, 0.75), count);
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const colour = new THREE.Color();
  let x = -w / 2 + 0.01;
  let n = 0;
  while (n < count && x < w / 2 - 0.03) {
    const bw = 0.022 + rand() * 0.03;
    const bh = maxH * (0.7 + rand() * 0.3);
    const bd = depth * (0.75 + rand() * 0.2);
    const lean = rand() < 0.08 ? 0.25 : 0;
    q.setFromEuler(new THREE.Euler(0, 0, lean));
    m.compose(new THREE.Vector3(x + bw / 2, bh / 2, 0), q, new THREE.Vector3(bw, bh, bd));
    mesh.setMatrixAt(n, m);
    mesh.setColorAt(n, colour.setHex(BOOK_COLOURS[Math.floor(rand() * BOOK_COLOURS.length)]));
    x += bw + 0.002 + (lean ? 0.03 : 0);
    n += 1;
  }
  mesh.count = n;
  return mesh;
}

function buildBookshelf(rand: Rand) {
  const g = new THREE.Group();
  const wood = mat(0x6b4a30, 0.7);
  const W = 0.9;
  const H = 1.7;
  const D = 0.3;
  add(g, box(0.03, H, D), wood, -W / 2, H / 2, 0);
  add(g, box(0.03, H, D), wood, W / 2, H / 2, 0);
  add(g, box(W, H, 0.015), wood, 0, H / 2, -D / 2);
  const shelves = 5;
  for (let i = 0; i < shelves; i += 1) {
    const y = 0.05 + (i * (H - 0.08)) / (shelves - 1);
    add(g, box(W, 0.025, D), wood, 0, y, 0);
    if (i < shelves - 1) {
      const row = booksRow(W - 0.06, D - 0.04, rand, (H - 0.08) / (shelves - 1) - 0.06);
      row.position.set(0, y + 0.0125, 0.01);
      g.add(row);
    }
  }
  return g;
}

function buildPlant(rand: Rand) {
  const g = new THREE.Group();
  const pot = lathe([[0, 0], [0.12, 0], [0.16, 0.26], [0.175, 0.28], [0.175, 0.3], [0, 0.3]], 24);
  add(g, pot, mat([0xb5653e, 0xe8e2d6, 0x3d5a6b][Math.floor(rand() * 3)], 0.8));
  add(g, cyl(0.16, 0.16, 0.01, 20), mat(0x3b2a1c, 1), 0, 0.285, 0);
  // Leaves: one shape, instanced, fanned out and up around the stems.
  const shape = new THREE.Shape();
  shape.moveTo(0, 0);
  shape.quadraticCurveTo(0.07, 0.1, 0, 0.26);
  shape.quadraticCurveTo(-0.07, 0.1, 0, 0);
  const leafGeo = new THREE.ShapeGeometry(shape, 6);
  const count = 18 + Math.floor(rand() * 10);
  const leaves = new THREE.InstancedMesh(leafGeo, mat(0x3f7d3a, 0.6, 0, { side: THREE.DoubleSide }), count);
  const m = new THREE.Matrix4();
  const colour = new THREE.Color();
  for (let i = 0; i < count; i += 1) {
    const h = 0.35 + rand() * 0.55;
    const a = rand() * Math.PI * 2;
    const tilt = 0.3 + rand() * 0.9;
    const obj = new THREE.Object3D();
    obj.position.set(Math.cos(a) * 0.04, h, Math.sin(a) * 0.04);
    obj.rotation.set(0, -a + Math.PI / 2, 0);
    obj.rotateX(-tilt);
    obj.scale.setScalar(0.8 + rand() * 0.6);
    obj.updateMatrix();
    m.copy(obj.matrix);
    leaves.setMatrixAt(i, m);
    leaves.setColorAt(i, colour.setHSL(0.28 + rand() * 0.06, 0.5, 0.25 + rand() * 0.12));
  }
  g.add(leaves);
  for (let i = 0; i < 3; i += 1) {
    const stem = add(g, cyl(0.008, 0.012, 0.7, 6), mat(0x4a6b2e, 0.8), (rand() - 0.5) * 0.06, 0.62, (rand() - 0.5) * 0.06);
    stem.rotation.set((rand() - 0.5) * 0.3, 0, (rand() - 0.5) * 0.3);
  }
  return g;
}

function buildEasel(board: EaselBoard) {
  const g = new THREE.Group();
  const wood = mat(0x8a6240, 0.7);
  for (const [x, z, rx, rz] of [[-0.3, 0.1, -0.12, 0.15], [0.3, 0.1, -0.12, -0.15], [0, -0.3, 0.3, 0]] as const) {
    const leg = add(g, box(0.035, 1.65, 0.035), wood, x, 0.8, z);
    leg.rotation.set(rx, 0, rz);
  }
  const map = boardTexture(board);
  const face = map ? mat(0xffffff, 0.8, 0, { map }) : mat(0xe8e8e0, 0.8);
  // Box faces are +x, -x, +y, -y, +z, -z: the drawing goes on the front (+z).
  const panel = add(g, box(0.72, 0.56, 0.02), [wood, wood, wood, wood, face, wood], 0, 1.12, 0.14);
  panel.rotation.x = -0.12;
  add(g, box(0.76, 0.03, 0.08), wood, 0, 0.82, 0.18);
  return g;
}

function buildFlag() {
  const g = new THREE.Group();
  add(g, cyl(0.16, 0.2, 0.08, 24), mat(0x2b2b2b, 0.5, 0.4), 0, 0.04, 0);
  add(g, cyl(0.014, 0.014, 1.85, 10), mat(0xc8c8c8, 0.3, 0.8), 0, 0.97, 0);
  add(g, sph(0.03, 12, 8), mat(0xd4af37, 0.3, 0.9), 0, 1.9, 0);
  // The Indian tricolour, as in a school's social-science room.
  const map = canvasTex('flag-in', 300, 200, (ctx, w, h) => {
    ctx.fillStyle = '#FF9933'; ctx.fillRect(0, 0, w, h / 3);
    ctx.fillStyle = '#FFFFFF'; ctx.fillRect(0, h / 3, w, h / 3);
    ctx.fillStyle = '#138808'; ctx.fillRect(0, (2 * h) / 3, w, h / 3);
    ctx.strokeStyle = '#000080'; ctx.lineWidth = 2;
    const r = h / 7.5;
    ctx.beginPath(); ctx.arc(w / 2, h / 2, r, 0, Math.PI * 2); ctx.stroke();
    for (let i = 0; i < 24; i += 1) {
      const a = (i * Math.PI * 2) / 24;
      ctx.beginPath(); ctx.moveTo(w / 2, h / 2); ctx.lineTo(w / 2 + Math.cos(a) * r, h / 2 + Math.sin(a) * r); ctx.stroke();
    }
  });
  const cloth = new THREE.PlaneGeometry(0.45, 0.3, 12, 1);
  const pos = cloth.getAttribute('position');
  for (let i = 0; i < pos.count; i += 1) pos.setZ(i, Math.sin((pos.getX(i) + 0.225) * 12) * 0.02 * (pos.getX(i) + 0.225));
  cloth.computeVertexNormals();
  add(g, cloth, map ? mat(0xffffff, 0.9, 0, { map, side: THREE.DoubleSide }) : mat(0xff9933, 0.9, 0, { side: THREE.DoubleSide }), 0.235, 1.68, 0);
  return g;
}

function buildPendulum() {
  const g = new THREE.Group();
  const steel = mat(0x9aa3ab, 0.35, 0.8);
  add(g, box(0.5, 0.05, 0.3), mat(0x333333, 0.6), 0, 0.025, 0);
  add(g, cyl(0.015, 0.015, 1.15, 10), steel, -0.2, 0.6, 0);
  add(g, cyl(0.015, 0.015, 1.15, 10), steel, 0.2, 0.6, 0);
  const bar = add(g, cyl(0.012, 0.012, 0.42, 10), steel, 0, 1.17, 0);
  bar.rotation.z = Math.PI / 2;
  const string = add(g, cyl(0.002, 0.002, 0.8, 4), mat(0xdddddd, 0.9), 0.08, 0.78, 0);
  string.rotation.z = 0.2;
  add(g, sph(0.05, 20, 14), mat(0xb08d3c, 0.3, 0.9), 0.16, 0.38, 0);
  return g;
}

function buildRobot(rand: Rand) {
  const g = new THREE.Group();
  const body = mat([0xe8e8e8, 0xf2c14e, 0x4fa3e0][Math.floor(rand() * 3)], 0.4, 0.2);
  const dark = mat(0x2b2f36, 0.5, 0.3);
  for (const x of [-0.12, 0.12]) {
    const wheel = add(g, cyl(0.07, 0.07, 0.05, 20), dark, x, 0.07, 0);
    wheel.rotation.z = Math.PI / 2;
  }
  add(g, box(0.26, 0.26, 0.2), body, 0, 0.26, 0);
  add(g, box(0.12, 0.08, 0.01), glow(0x39d98a), 0, 0.28, 0.105);
  add(g, box(0.2, 0.16, 0.17), body, 0, 0.5, 0);
  add(g, sph(0.022), glow(0x4fd1ff), -0.05, 0.52, 0.086);
  add(g, sph(0.022), glow(0x4fd1ff), 0.05, 0.52, 0.086);
  add(g, cyl(0.006, 0.006, 0.1, 6), dark, 0, 0.63, 0);
  add(g, sph(0.018), glow(0xff5a5a), 0, 0.69, 0);
  for (const x of [-0.16, 0.16]) {
    const arm = add(g, box(0.04, 0.18, 0.04), body, x, 0.3, 0);
    arm.rotation.z = x > 0 ? -0.3 : 0.3;
  }
  return g;
}

const BLOCK_COLOURS = ['#e84a5f', '#2a9df4', '#f7b32b', '#43aa8b', '#8e6bd6'];

function buildBlocks(size: number, count: number, rand: Rand, chars: string) {
  const g = new THREE.Group();
  const geo = box(size, size, size);
  for (let i = 0; i < count; i += 1) {
    const ch = chars[Math.floor(rand() * chars.length)];
    const bg = BLOCK_COLOURS[Math.floor(rand() * BLOCK_COLOURS.length)];
    const map = faceTexture(ch, bg);
    const m = add(g, geo, map ? mat(0xffffff, 0.6, 0, { map }) : mat(0xe84a5f, 0.6));
    const stacked = i >= 3;
    m.position.set((i % 3) * size * 1.05 - size * 1.05 + (stacked ? size * 0.5 : 0), size / 2 + (stacked ? size : 0), (rand() - 0.5) * size * 0.3);
    m.rotation.y = (rand() - 0.5) * 0.5;
  }
  return g;
}

// ---------------------------------------------------------------------------
// Small pieces (on side tables)
// ---------------------------------------------------------------------------

function buildCrystals(rand: Rand) {
  const g = new THREE.Group();
  add(g, rockGeometry(0.07, rand), mat(0x6b625a, 0.9, 0, { flatShading: true }), 0, 0.03, 0);
  const tint = [0x9b6bd6, 0xe8f4ff, 0xf2a0c0, 0x7fd1c7][Math.floor(rand() * 4)];
  const crystal = mat(tint, 0.15, 0.1, { transparent: true, opacity: 0.8, flatShading: true });
  const n = 5 + Math.floor(rand() * 4);
  for (let i = 0; i < n; i += 1) {
    const h = 0.05 + rand() * 0.09;
    const c = new THREE.Group();
    add(c, cyl(0.012, 0.014, h, 6), crystal, 0, h / 2, 0);
    add(c, new THREE.ConeGeometry(0.012, 0.03, 6), crystal, 0, h + 0.015, 0);
    c.position.set((rand() - 0.5) * 0.06, 0.04, (rand() - 0.5) * 0.06);
    c.rotation.set((rand() - 0.5) * 0.9, rand() * 3, (rand() - 0.5) * 0.9);
    g.add(c);
  }
  return g;
}

function buildCompass() {
  const g = new THREE.Group();
  add(g, cyl(0.07, 0.07, 0.025, 32), mat(0xb08d3c, 0.3, 0.9), 0, 0.0125, 0);
  const map = canvasTex('compass-dial', 256, 256, (ctx, w, h) => {
    ctx.fillStyle = '#f4ecd8'; ctx.beginPath(); ctx.arc(w / 2, h / 2, w / 2, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#222'; ctx.font = 'bold 36px serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('N', w / 2, 34); ctx.fillText('S', w / 2, h - 34); ctx.fillText('E', w - 34, h / 2); ctx.fillText('W', 34, h / 2);
    ctx.fillStyle = '#c0392b'; ctx.beginPath(); ctx.moveTo(w / 2, 60); ctx.lineTo(w / 2 + 12, h / 2); ctx.lineTo(w / 2 - 12, h / 2); ctx.fill();
    ctx.fillStyle = '#444'; ctx.beginPath(); ctx.moveTo(w / 2, h - 60); ctx.lineTo(w / 2 + 12, h / 2); ctx.lineTo(w / 2 - 12, h / 2); ctx.fill();
  });
  const face = add(g, new THREE.CircleGeometry(0.062, 32), map ? mat(0xffffff, 0.5, 0, { map }) : mat(0xf4ecd8, 0.5), 0, 0.026, 0);
  face.rotation.x = -Math.PI / 2;
  return g;
}

function buildScrolls(rand: Rand) {
  const g = new THREE.Group();
  const map = parchment('parchment');
  const paper = map ? mat(0xffffff, 0.9, 0, { map }) : mat(0xe8d6aa, 0.9);
  const knob = mat(0x4a2e1a, 0.6);
  const n = 2 + Math.floor(rand() * 3);
  for (let i = 0; i < n; i += 1) {
    const s = new THREE.Group();
    const len = 0.22 + rand() * 0.1;
    const roll = add(s, cyl(0.025, 0.025, len, 16), paper);
    roll.rotation.z = Math.PI / 2;
    for (const side of [-1, 1]) {
      const k = add(s, cyl(0.012, 0.012, 0.03, 10), knob, side * (len / 2 + 0.015), 0, 0);
      k.rotation.z = Math.PI / 2;
    }
    s.position.set((rand() - 0.5) * 0.05, 0.025 + (i >= 2 ? 0.05 : 0), (i % 2) * 0.055 - 0.03);
    s.rotation.y = (rand() - 0.5) * 0.4;
    g.add(s);
  }
  return g;
}

function buildOilLamp() {
  const g = new THREE.Group();
  add(g, lathe([[0, 0], [0.04, 0], [0.07, 0.025], [0.065, 0.045], [0.02, 0.05], [0.015, 0.055], [0, 0.055]], 24), mat(0xa65a35, 0.85));
  const spout = add(g, cyl(0.012, 0.018, 0.06, 10), mat(0xa65a35, 0.85), 0.07, 0.03, 0);
  spout.rotation.z = Math.PI / 2 - 0.3;
  add(g, new THREE.ConeGeometry(0.012, 0.04, 10), glow(0xffa634), 0.1, 0.065, 0);
  return g;
}

function buildCoins(rand: Rand) {
  const g = new THREE.Group();
  const metals = [mat(0xd4af37, 0.3, 0.9), mat(0xb87333, 0.35, 0.85), mat(0xb8b8b8, 0.3, 0.9)];
  const coin = cyl(0.018, 0.018, 0.004, 20);
  for (let s = 0; s < 3; s += 1) {
    const h = 3 + Math.floor(rand() * 7);
    const m = metals[Math.floor(rand() * metals.length)];
    for (let i = 0; i < h; i += 1) add(g, coin, m, s * 0.045 - 0.045 + (rand() - 0.5) * 0.004, 0.002 + i * 0.0042, (rand() - 0.5) * 0.004);
  }
  return g;
}

function buildSundial() {
  const g = new THREE.Group();
  add(g, cyl(0.1, 0.11, 0.03, 32), mat(0x9a948a, 0.8), 0, 0.015, 0);
  const map = canvasTex('sundial', 256, 256, (ctx, w, h) => {
    ctx.fillStyle = '#b8a888'; ctx.beginPath(); ctx.arc(w / 2, h / 2, w / 2, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = '#3d3325'; ctx.lineWidth = 3;
    for (let i = 0; i <= 12; i += 1) {
      const a = Math.PI + (i * Math.PI) / 12;
      ctx.beginPath(); ctx.moveTo(w / 2, h / 2); ctx.lineTo(w / 2 + Math.cos(a) * 110, h / 2 + Math.sin(a) * 110); ctx.stroke();
    }
  });
  const face = add(g, new THREE.CircleGeometry(0.095, 32), map ? mat(0xffffff, 0.7, 0, { map }) : mat(0xb8a888, 0.7), 0, 0.031, 0);
  face.rotation.x = -Math.PI / 2;
  const tri = new THREE.Shape();
  tri.moveTo(0, 0); tri.lineTo(0.08, 0); tri.lineTo(0, 0.07); tri.lineTo(0, 0);
  const gnomon = add(g, new THREE.ExtrudeGeometry(tri, { depth: 0.004, bevelEnabled: false }), mat(0x6b5a3a, 0.4, 0.7), -0.04, 0.031, -0.002);
  gnomon.rotation.y = 0;
  return g;
}

function buildBookStack(rand: Rand) {
  const g = new THREE.Group();
  let y = 0;
  const n = 3 + Math.floor(rand() * 3);
  for (let i = 0; i < n; i += 1) {
    const w = 0.16 + rand() * 0.07;
    const d = 0.12 + rand() * 0.05;
    const h = 0.025 + rand() * 0.03;
    const b = add(g, box(w, h, d), mat(BOOK_COLOURS[Math.floor(rand() * BOOK_COLOURS.length)], 0.75), (rand() - 0.5) * 0.02, y + h / 2, 0);
    b.rotation.y = (rand() - 0.5) * 0.5;
    // Page edges, on the book so they turn with it.
    add(b, box(w * 0.96, h * 0.8, 0.004), mat(0xf2ecd9, 0.9), 0, 0, d / 2 - 0.001);
    y += h;
  }
  return g;
}

function buildInkwell() {
  const g = new THREE.Group();
  add(g, lathe([[0, 0], [0.035, 0], [0.04, 0.03], [0.02, 0.05], [0.015, 0.06], [0, 0.06]], 20), glass(0x1b2440));
  add(g, cyl(0.03, 0.033, 0.02, 20), mat(0x0d1330, 0.2), 0, 0.012, 0);
  // Quill: a long, thin, white feather.
  const shape = new THREE.Shape();
  shape.moveTo(0, 0);
  shape.quadraticCurveTo(0.018, 0.12, 0.004, 0.22);
  shape.quadraticCurveTo(-0.01, 0.12, 0, 0);
  const quill = add(g, new THREE.ShapeGeometry(shape, 8), mat(0xf5f2ea, 0.8, 0, { side: THREE.DoubleSide }), 0, 0.05, 0);
  quill.rotation.set(0, 0.6, -0.35);
  // An open sheet beside it.
  const map = parchment('parchment');
  const sheet = add(g, new THREE.PlaneGeometry(0.16, 0.2), map ? mat(0xffffff, 0.9, 0, { map }) : mat(0xe8d6aa, 0.9), 0.12, 0.002, 0.02);
  sheet.rotation.set(-Math.PI / 2, 0, 0.2);
  return g;
}

function buildReadingLamp() {
  const g = new THREE.Group();
  const brass = mat(0xb08d3c, 0.3, 0.85);
  add(g, cyl(0.07, 0.08, 0.02, 24), brass, 0, 0.01, 0);
  add(g, cyl(0.008, 0.008, 0.3, 8), brass, 0, 0.16, 0);
  const shade = add(g, cyl(0.04, 0.09, 0.09, 24, ), mat(0x1f5d3a, 0.5), 0, 0.32, 0.03);
  shade.rotation.x = 0.4;
  add(g, sph(0.02, 12, 8), glow(0xfff2c4), 0, 0.29, 0.04);
  return g;
}

function buildMicroscope() {
  const g = new THREE.Group();
  const body = mat(0xf0f0f0, 0.35, 0.1);
  const dark = mat(0x222428, 0.4, 0.5);
  add(g, box(0.14, 0.03, 0.18), body, 0, 0.015, 0);
  add(g, box(0.04, 0.22, 0.05), body, 0, 0.13, -0.06);
  add(g, box(0.1, 0.012, 0.1), dark, 0, 0.1, 0.02);
  const tube = add(g, cyl(0.02, 0.02, 0.14, 16), dark, 0, 0.2, 0.0);
  tube.rotation.x = -0.35;
  add(g, cyl(0.013, 0.013, 0.04, 12), dark, 0, 0.28, -0.03).rotation.x = -0.35;
  add(g, cyl(0.009, 0.006, 0.04, 10), mat(0x9aa3ab, 0.3, 0.8), 0, 0.135, 0.03);
  add(g, box(0.06, 0.002, 0.025), glass(), 0, 0.108, 0.02);
  return g;
}

function buildSpecimenJars(rand: Rand) {
  const g = new THREE.Group();
  const n = 2 + Math.floor(rand() * 2);
  const jar = lathe([[0, 0], [0.04, 0], [0.042, 0.005], [0.042, 0.12], [0.03, 0.13], [0, 0.13]], 20);
  for (let i = 0; i < n; i += 1) {
    const j = new THREE.Group();
    add(j, jar, glass());
    add(j, cyl(0.038, 0.038, 0.1, 20), mat([0xd9c86b, 0xb5d99c, 0xe0b48a][i % 3], 0.2, 0, { transparent: true, opacity: 0.45, depthWrite: false }), 0, 0.055, 0);
    const spec = add(j, sph(0.02, 12, 10), mat([0xc27b6a, 0x7a9a5a, 0x9a6a8a][Math.floor(rand() * 3)], 0.6), 0, 0.05, 0);
    spec.scale.set(1, 1.5, 0.8);
    add(j, cyl(0.032, 0.032, 0.015, 20), mat(0x2b2b2b, 0.5), 0, 0.137, 0);
    j.position.set(i * 0.1 - (n - 1) * 0.05, 0, (rand() - 0.5) * 0.03);
    g.add(j);
  }
  return g;
}

function buildDna() {
  const g = new THREE.Group();
  add(g, cyl(0.06, 0.07, 0.02, 20), mat(0x2b2b2b, 0.5), 0, 0.01, 0);
  add(g, cyl(0.004, 0.004, 0.3, 6), mat(0x9aa3ab, 0.3, 0.8), 0, 0.16, 0);
  const steps = 16;
  const spheres = new THREE.InstancedMesh(sph(0.012, 10, 8), mat(0xffffff, 0.4), steps * 2);
  const rungs = new THREE.InstancedMesh(cyl(0.003, 0.003, 1, 5), mat(0xffffff, 0.6), steps);
  const colour = new THREE.Color();
  const pairColours = [0xe84a5f, 0x2a9df4, 0xf7b32b, 0x43aa8b];
  const m = new THREE.Matrix4();
  for (let i = 0; i < steps; i += 1) {
    const y = 0.04 + i * 0.016;
    const a = i * 0.55;
    const r = 0.04;
    const p1 = new THREE.Vector3(Math.cos(a) * r, y, Math.sin(a) * r);
    const p2 = new THREE.Vector3(-Math.cos(a) * r, y, -Math.sin(a) * r);
    spheres.setMatrixAt(i * 2, m.makeTranslation(p1.x, p1.y, p1.z));
    spheres.setMatrixAt(i * 2 + 1, m.makeTranslation(p2.x, p2.y, p2.z));
    spheres.setColorAt(i * 2, colour.setHex(0x5b6c8f));
    spheres.setColorAt(i * 2 + 1, colour.setHex(0x5b6c8f));
    const o = new THREE.Object3D();
    o.position.set(0, y, 0);
    o.rotation.set(0, -a, Math.PI / 2);
    o.scale.set(1, r * 2, 1);
    o.updateMatrix();
    rungs.setMatrixAt(i, o.matrix);
    rungs.setColorAt(i, colour.setHex(pairColours[i % 4]));
  }
  g.add(spheres, rungs);
  return g;
}

function buildFlasks(rand: Rand) {
  const g = new THREE.Group();
  const liquids = [0x3fb6e8, 0xe84a8f, 0x7ad35a, 0xf2c14e, 0x9b6bd6];
  const liquid = () => mat(liquids[Math.floor(rand() * liquids.length)], 0.2, 0, { transparent: true, opacity: 0.75 });
  // Erlenmeyer
  const e = new THREE.Group();
  add(e, lathe([[0, 0], [0.06, 0], [0.062, 0.005], [0.02, 0.1], [0.018, 0.15], [0.02, 0.155], [0, 0.155]], 24), glass());
  add(e, lathe([[0, 0.003], [0.058, 0.003], [0.035, 0.06], [0, 0.06]], 24), liquid());
  g.add(e);
  // Round-bottom on a cork ring
  const r = new THREE.Group();
  add(r, cyl(0.035, 0.04, 0.015, 16), mat(0xa0784f, 0.9), 0, 0.0075, 0);
  add(r, sph(0.05, 20, 14), glass(), 0, 0.06, 0);
  add(r, cyl(0.013, 0.013, 0.08, 12), glass(), 0, 0.14, 0);
  add(r, sph(0.045, 18, 12, ), liquid(), 0, 0.05, 0).scale.set(1, 0.6, 1);
  r.position.set(0.13, 0, -0.02);
  g.add(r);
  // Beaker
  const b = new THREE.Group();
  add(b, cyl(0.04, 0.04, 0.1, 24, ), glass(), 0, 0.05, 0);
  add(b, cyl(0.037, 0.037, 0.05, 24), liquid(), 0, 0.027, 0);
  b.position.set(-0.12, 0, 0.03);
  g.add(b);
  return g;
}

function buildTubeRack(rand: Rand) {
  const g = new THREE.Group();
  const wood = mat(0xc9a36b, 0.7);
  add(g, box(0.26, 0.015, 0.07), wood, 0, 0.0075, 0);
  add(g, box(0.26, 0.012, 0.07), wood, 0, 0.09, 0);
  for (const x of [-0.125, 0.125]) add(g, box(0.012, 0.1, 0.07), wood, x, 0.05, 0);
  const liquids = [0x3fb6e8, 0xe84a8f, 0x7ad35a, 0xf2c14e, 0x9b6bd6, 0xff8c42];
  for (let i = 0; i < 5; i += 1) {
    const x = -0.09 + i * 0.045;
    add(g, cyl(0.011, 0.011, 0.15, 12), glass(), x, 0.1, 0);
    const h = 0.03 + rand() * 0.06;
    add(g, cyl(0.009, 0.009, h, 10), mat(liquids[Math.floor(rand() * liquids.length)], 0.2, 0, { transparent: true, opacity: 0.8 }), x, 0.03 + h / 2, 0);
  }
  return g;
}

function buildMolecule(rand: Rand) {
  const g = new THREE.Group();
  add(g, cyl(0.05, 0.06, 0.015, 20), mat(0x2b2b2b, 0.5), 0, 0.0075, 0);
  add(g, cyl(0.004, 0.004, 0.08, 6), mat(0x9aa3ab, 0.3, 0.8), 0, 0.055, 0);
  const centre = new THREE.Vector3(0, 0.13, 0);
  const water = rand() < 0.5;
  add(g, sph(water ? 0.035 : 0.03, 20, 14), mat(water ? 0xe84a4a : 0x333333, 0.35), centre.x, centre.y, centre.z);
  const dirs = water
    ? [new THREE.Vector3(0.8, -0.6, 0), new THREE.Vector3(-0.8, -0.6, 0)]
    : [new THREE.Vector3(1, 1, 1), new THREE.Vector3(-1, -1, 1), new THREE.Vector3(-1, 1, -1), new THREE.Vector3(1, -1, -1)];
  const white = mat(0xf2f2f2, 0.35);
  const stick = mat(0xbbbbbb, 0.4);
  for (const d of dirs) {
    d.normalize();
    const p = centre.clone().addScaledVector(d, 0.065);
    add(g, sph(0.02, 16, 12), white, p.x, p.y, p.z);
    const mid = centre.clone().addScaledVector(d, 0.032);
    const s = add(g, cyl(0.005, 0.005, 0.065, 8), stick, mid.x, mid.y, mid.z);
    s.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), d);
  }
  return g;
}

function buildPrism() {
  const g = new THREE.Group();
  const p = add(g, cyl(0.05, 0.05, 0.1, 3), glass(0xf2fbff), 0, 0.043, 0);
  p.rotation.z = Math.PI / 2;
  const map = canvasTex('rainbow', 256, 32, (ctx, w, h) => {
    const g2 = ctx.createLinearGradient(0, 0, 0, h);
    ['#ff0000', '#ff8000', '#ffff00', '#00c000', '#0080ff', '#4b0082', '#8f00ff'].forEach((c, i, a) => g2.addColorStop(i / (a.length - 1), c));
    ctx.fillStyle = g2;
    ctx.fillRect(0, 0, w, h);
  });
  const fan = add(g, new THREE.PlaneGeometry(0.16, 0.05), map ? mat(0xffffff, 0.9, 0, { map, transparent: true, opacity: 0.85, emissive: 0x222222 }) : mat(0xff8000, 0.9), 0.14, 0.002, 0.02);
  fan.rotation.set(-Math.PI / 2, 0, -0.3);
  return g;
}

function buildNewtonsCradle() {
  const g = new THREE.Group();
  const chrome = mat(0xdfe3e8, 0.12, 1);
  add(g, box(0.22, 0.015, 0.12), mat(0x1f2328, 0.4), 0, 0.0075, 0);
  for (const x of [-0.1, 0.1]) {
    for (const z of [-0.05, 0.05]) add(g, cyl(0.004, 0.004, 0.16, 6), chrome, x, 0.09, z);
  }
  for (const z of [-0.05, 0.05]) {
    const bar = add(g, cyl(0.004, 0.004, 0.2, 6), chrome, 0, 0.17, z);
    bar.rotation.z = Math.PI / 2;
  }
  for (let i = 0; i < 5; i += 1) {
    const x = -0.048 + i * 0.024;
    add(g, sph(0.012, 16, 12), chrome, x, 0.06, 0);
    for (const z of [-0.05, 0.05]) {
      const s = add(g, cyl(0.0008, 0.0008, 0.12, 3), mat(0xcccccc, 0.9), x, 0.115, z / 2);
      s.rotation.x = z > 0 ? -0.4 : 0.4;
    }
  }
  return g;
}

function buildMagnets() {
  const g = new THREE.Group();
  // Standing on its two poles: the arc on top, the silver pole pieces on the table.
  const horseshoe = add(g, new THREE.TorusGeometry(0.05, 0.014, 10, 24, Math.PI), mat(0xd0342c, 0.4), 0, 0.04, 0);
  horseshoe.rotation.z = 0;
  for (const x of [-0.05, 0.05]) add(g, box(0.028, 0.04, 0.028), mat(0xc8c8c8, 0.3, 0.8), x, 0.02, 0);
  const bar = new THREE.Group();
  add(bar, box(0.06, 0.02, 0.025), mat(0xd0342c, 0.4), -0.03, 0.01, 0);
  add(bar, box(0.06, 0.02, 0.025), mat(0x2a6fd0, 0.4), 0.03, 0.01, 0);
  bar.position.set(0.02, 0, 0.08);
  bar.rotation.y = 0.4;
  g.add(bar);
  return g;
}

function buildSolids(rand: Rand) {
  const g = new THREE.Group();
  const colours = [0xe84a5f, 0x2a9df4, 0xf7b32b, 0x43aa8b, 0x8e6bd6, 0xe8e2d6];
  const shapes: THREE.BufferGeometry[] = [
    box(0.06, 0.06, 0.06),
    new THREE.ConeGeometry(0.035, 0.08, 24),
    sph(0.035, 20, 14),
    new THREE.ConeGeometry(0.045, 0.07, 4),
    cyl(0.03, 0.03, 0.07, 24),
    new THREE.DodecahedronGeometry(0.035),
    new THREE.OctahedronGeometry(0.04),
  ];
  const n = 4 + Math.floor(rand() * 2);
  for (let i = 0; i < n; i += 1) {
    const geo = shapes[(i + Math.floor(rand() * shapes.length)) % shapes.length];
    geo.computeBoundingBox();
    const hb = geo.boundingBox!;
    const m = add(g, geo, mat(colours[Math.floor(rand() * colours.length)], 0.45, 0, { flatShading: geo.type !== 'SphereGeometry' }));
    m.position.set(((i % 3) - 1) * 0.09, -hb.min.y, i < 3 ? -0.04 : 0.05);
    m.rotation.y = rand() * Math.PI;
  }
  return g;
}

function buildAbacus(rand: Rand) {
  const g = new THREE.Group();
  const wood = mat(0x8a6240, 0.6);
  add(g, box(0.26, 0.015, 0.05), wood, 0, 0.0075, 0);
  for (const x of [-0.125, 0.125]) add(g, box(0.015, 0.18, 0.05), wood, x, 0.09, 0);
  add(g, box(0.26, 0.015, 0.05), wood, 0, 0.175, 0);
  const rods = 6;
  const beads = new THREE.InstancedMesh(new THREE.SphereGeometry(0.011, 8, 6).scale(1.3, 0.9, 1.3), mat(0xffffff, 0.35), rods * 8);
  const colour = new THREE.Color();
  const palette = [0xe84a5f, 0x2a9df4, 0xf7b32b, 0x43aa8b, 0x8e6bd6, 0xff8c42];
  const m = new THREE.Matrix4();
  let n = 0;
  for (let r = 0; r < rods; r += 1) {
    const y = 0.03 + r * 0.025;
    const rod = add(g, cyl(0.002, 0.002, 0.24, 5), mat(0xbbbbbb, 0.3, 0.8), 0, y, 0);
    rod.rotation.z = Math.PI / 2;
    const left = Math.floor(rand() * 9);
    for (let b = 0; b < 8; b += 1) {
      const x = b < left ? -0.105 + b * 0.024 : 0.105 - (7 - b) * 0.024;
      beads.setMatrixAt(n, m.makeTranslation(x, y, 0));
      beads.setColorAt(n, colour.setHex(palette[r % palette.length]));
      n += 1;
    }
  }
  g.add(beads);
  return g;
}

function buildSetSquares() {
  const g = new THREE.Group();
  const plastic = mat(0x9fd8ff, 0.2, 0, { transparent: true, opacity: 0.55, depthWrite: false });
  const tri = new THREE.Shape();
  tri.moveTo(0, 0); tri.lineTo(0.18, 0); tri.lineTo(0, 0.12); tri.lineTo(0, 0);
  const t = add(g, new THREE.ExtrudeGeometry(tri, { depth: 0.003, bevelEnabled: false }), plastic, -0.1, 0.004, 0.06);
  t.rotation.x = -Math.PI / 2;
  const pro = add(g, new THREE.RingGeometry(0.05, 0.075, 32, 1, 0, Math.PI), mat(0xfff3b0, 0.2, 0, { transparent: true, opacity: 0.6, side: THREE.DoubleSide, depthWrite: false }), 0.08, 0.004, 0.02);
  pro.rotation.x = -Math.PI / 2;
  add(g, box(0.25, 0.003, 0.03), mat(0xf2e3b3, 0.6), 0, 0.0015, -0.06);
  return g;
}

function buildMagnifier() {
  const g = new THREE.Group();
  const holder = new THREE.Group();
  add(holder, new THREE.TorusGeometry(0.045, 0.006, 8, 32), mat(0x2b2b2b, 0.4, 0.4));
  add(holder, new THREE.CircleGeometry(0.043, 32), glass(0xeaf6ff));
  const handle = add(holder, cyl(0.009, 0.011, 0.1, 12), mat(0x6b4a30, 0.6), 0, -0.095, 0);
  handle.rotation.z = 0;
  holder.rotation.set(-Math.PI / 2 + 0.08, 0, 0.6);
  holder.position.y = 0.012;
  g.add(holder);
  return g;
}

function buildWateringCan() {
  const g = new THREE.Group();
  const tin = mat(0x5fae6e, 0.4, 0.4);
  add(g, cyl(0.06, 0.065, 0.12, 24), tin, 0, 0.06, 0);
  const spout = add(g, cyl(0.008, 0.014, 0.16, 10), tin, 0.1, 0.1, 0);
  spout.rotation.z = -0.9;
  const handle = add(g, new THREE.TorusGeometry(0.045, 0.007, 6, 16, Math.PI), tin, -0.02, 0.12, 0);
  handle.rotation.z = 0.2;
  return g;
}

function buildMonitor() {
  const g = new THREE.Group();
  const dark = mat(0x1f2328, 0.4, 0.3);
  add(g, box(0.12, 0.01, 0.08), dark, 0, 0.005, -0.04);
  add(g, box(0.02, 0.12, 0.02), dark, 0, 0.065, -0.05);
  add(g, box(0.3, 0.19, 0.015), dark, 0, 0.2, -0.04);
  const map = canvasTex('code-screen', 256, 160, (ctx, w, h) => {
    const rand = seededRandom(9);
    ctx.fillStyle = '#0f1720';
    ctx.fillRect(0, 0, w, h);
    const colours = ['#7ee787', '#79c0ff', '#ffa657', '#d2a8ff', '#c9d1d9'];
    for (let y = 12; y < h - 6; y += 11) {
      let x = 10 + Math.floor(rand() * 3) * 12;
      while (x < w - 20 && rand() > 0.15) {
        const len = 12 + rand() * 40;
        ctx.fillStyle = colours[Math.floor(rand() * colours.length)];
        ctx.fillRect(x, y, len, 5);
        x += len + 6;
      }
    }
  });
  add(g, new THREE.PlaneGeometry(0.28, 0.17), map ? mat(0xffffff, 0.4, 0, { map, emissive: 0xffffff, emissiveMap: map, emissiveIntensity: 0.8 }) : glow(0x1f6feb), 0, 0.2, -0.032);
  add(g, box(0.26, 0.012, 0.09), mat(0x2b2f36, 0.5), 0, 0.006, 0.07);
  return g;
}

function buildCircuitBoard(rand: Rand) {
  const g = new THREE.Group();
  add(g, box(0.2, 0.008, 0.14), mat(0x1e6b3a, 0.5), 0, 0.004, 0);
  const chip = mat(0x1a1a1a, 0.4);
  for (let i = 0; i < 5; i += 1) add(g, box(0.02 + rand() * 0.03, 0.006, 0.02 + rand() * 0.02), chip, (rand() - 0.5) * 0.15, 0.011, (rand() - 0.5) * 0.09);
  for (let i = 0; i < 4; i += 1) add(g, cyl(0.006, 0.006, 0.02, 10), mat([0x2a6fd0, 0x333333][i % 2], 0.4), (rand() - 0.5) * 0.15, 0.018, (rand() - 0.5) * 0.09);
  add(g, sph(0.005, 8, 6), glow(0xff3b3b), 0.08, 0.012, 0.05);
  return g;
}

function buildBallotBox() {
  const g = new THREE.Group();
  add(g, box(0.18, 0.16, 0.14), mat(0x2d4a7a, 0.5), 0, 0.08, 0);
  add(g, box(0.07, 0.004, 0.012), mat(0x111111, 0.6), 0, 0.161, 0);
  const paper = add(g, box(0.05, 0.001, 0.07), mat(0xf2f2f2, 0.9), 0, 0.2, 0);
  paper.rotation.set(0.5, 0.3, 0);
  return g;
}


// ---------------------------------------------------------------------------
// More floor pieces, so each topic of a chapter can have its own
// ---------------------------------------------------------------------------

function buildVolcano(rand: Rand) {
  const g = new THREE.Group();
  add(g, box(0.8, 0.05, 0.7), mat(0x6b4a30, 0.8), 0, 0.025, 0);
  const cone = lathe([[0, 0.05], [0.38, 0.05], [0.3, 0.2], [0.2, 0.38], [0.11, 0.52], [0.075, 0.55], [0.05, 0.5], [0, 0.49]], 28);
  // Rough the slopes so it reads as rock, not a turned cone.
  const pos = cone.getAttribute('position');
  for (let i = 0; i < pos.count; i += 1) {
    const y = pos.getY(i);
    if (y < 0.06 || y > 0.54) continue;
    const k = 0.9 + rand() * 0.2;
    pos.setX(i, pos.getX(i) * k);
    pos.setZ(i, pos.getZ(i) * k);
  }
  cone.computeVertexNormals();
  add(g, cone, mat(0x5a4636, 0.95, 0, { flatShading: true }));
  add(g, cyl(0.05, 0.05, 0.01, 16), glow(0xff5a1f), 0, 0.5, 0);
  // Two lava flows down the front.
  for (const [a, len] of [[0.2, 0.34], [-0.35, 0.26]] as const) {
    const flow = add(g, box(0.03, len, 0.012), glow(0xff7a1f), Math.sin(a) * 0.17, 0.33, 0.17 + Math.cos(a) * 0.02);
    flow.rotation.set(-0.72, a, 0);
  }
  return g;
}

function buildWeatherStation() {
  const g = new THREE.Group();
  const white = mat(0xf2f2ee, 0.5);
  const steel = mat(0x9aa3ab, 0.35, 0.8);
  add(g, cyl(0.16, 0.2, 0.04, 20), mat(0x444444, 0.7), 0, 0.02, 0);
  add(g, cyl(0.018, 0.018, 1.45, 10), steel, 0, 0.75, 0);
  // Anemometer: three cups on arms.
  for (let i = 0; i < 3; i += 1) {
    const a = (i * Math.PI * 2) / 3;
    const arm = add(g, cyl(0.004, 0.004, 0.16, 6), steel, Math.cos(a) * 0.08, 1.5, Math.sin(a) * 0.08);
    arm.rotation.set(0, -a, Math.PI / 2);
    const cup = add(g, new THREE.SphereGeometry(0.03, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2), white, Math.cos(a) * 0.16, 1.5, Math.sin(a) * 0.16);
    cup.rotation.set(0, -a, Math.PI / 2);
  }
  // Wind vane.
  const vane = add(g, box(0.24, 0.01, 0.01), steel, 0, 1.38, 0);
  vane.rotation.y = 0.6;
  add(g, new THREE.ConeGeometry(0.02, 0.05, 8), mat(0xd0342c, 0.5), 0.1, 1.38, -0.07).rotation.z = -Math.PI / 2;
  // Stevenson screen: the white louvred box on its own leg.
  add(g, cyl(0.012, 0.012, 0.9, 8), steel, 0.2, 0.45, 0);
  add(g, box(0.2, 0.18, 0.16), white, 0.2, 0.99, 0);
  for (let i = 0; i < 4; i += 1) add(g, box(0.202, 0.008, 0.162), mat(0xd9d9d4, 0.5), 0.2, 0.92 + i * 0.045, 0);
  // Rain gauge.
  add(g, cyl(0.045, 0.045, 0.22, 16), mat(0x2a6fd0, 0.3, 0, { transparent: true, opacity: 0.6 }), -0.18, 0.11, 0.05);
  return g;
}

function buildTree(rand: Rand) {
  const g = new THREE.Group();
  add(g, box(0.5, 0.3, 0.5), mat(0x6b4a30, 0.8), 0, 0.15, 0);
  add(g, box(0.46, 0.01, 0.46), mat(0x3b2a1c, 1), 0, 0.3, 0);
  const trunk = add(g, cyl(0.035, 0.06, 0.9, 10), mat(0x5a3d25, 0.95), 0, 0.75, 0);
  trunk.rotation.z = (rand() - 0.5) * 0.08;
  const leaf = [0x3f7d3a, 0x4f8f3f, 0x356b30];
  for (let i = 0; i < 6; i += 1) {
    const r = 0.16 + rand() * 0.09;
    const a = rand() * Math.PI * 2;
    const d = i === 0 ? 0 : 0.12 + rand() * 0.1;
    const blob = add(g, new THREE.IcosahedronGeometry(r, 1), mat(leaf[i % 3], 0.8, 0, { flatShading: true }), Math.cos(a) * d, 1.3 + rand() * 0.25, Math.sin(a) * d);
    blob.rotation.set(rand(), rand(), rand());
  }
  return g;
}

function buildTelescope() {
  const g = new THREE.Group();
  const wood = mat(0x8a6240, 0.6);
  for (let i = 0; i < 3; i += 1) {
    const a = (i * Math.PI * 2) / 3;
    const leg = add(g, cyl(0.014, 0.018, 1.0, 8), wood, Math.cos(a) * 0.2, 0.48, Math.sin(a) * 0.2);
    leg.rotation.set(Math.sin(a) * 0.38, 0, -Math.cos(a) * 0.38);
  }
  add(g, cyl(0.04, 0.04, 0.05, 12), mat(0x2b2b2b, 0.4, 0.6), 0, 0.97, 0);
  const tube = new THREE.Group();
  tube.position.set(0, 1.03, 0);
  tube.rotation.x = -0.6;
  add(tube, cyl(0.05, 0.05, 0.8, 24), mat(0xf2f2ee, 0.35, 0.2));
  add(tube, cyl(0.056, 0.056, 0.06, 24), mat(0x2b2b2b, 0.4, 0.5), 0, 0.38, 0);
  add(tube, cyl(0.015, 0.015, 0.08, 10), mat(0x2b2b2b, 0.4, 0.5), 0, -0.42, 0);
  add(tube, cyl(0.014, 0.014, 0.22, 10), mat(0x2b2b2b, 0.4, 0.5), 0.07, 0.1, 0);
  g.add(tube);
  return g;
}

function buildTreasureChest(rand: Rand) {
  const g = new THREE.Group();
  const wood = mat(0x6b4226, 0.8);
  const iron = mat(0x3a3a3a, 0.5, 0.7);
  add(g, box(0.6, 0.3, 0.38), wood, 0, 0.15, 0);
  const lid = add(g, new THREE.CylinderGeometry(0.19, 0.19, 0.6, 20, 1, false, 0, Math.PI), wood, 0, 0.3, 0);
  // A half cylinder along the chest's length, curved side up.
  lid.rotation.set(0, 0, Math.PI / 2);
  for (const x of [-0.22, 0, 0.22]) {
    add(g, box(0.035, 0.305, 0.385), iron, x, 0.152, 0);
  }
  add(g, box(0.07, 0.08, 0.02), mat(0xd4af37, 0.3, 0.9), 0, 0.26, 0.2);
  // A few coins spilled in front.
  const coin = cyl(0.02, 0.02, 0.005, 16);
  for (let i = 0; i < 5; i += 1) add(g, coin, mat(0xd4af37, 0.3, 0.9), (rand() - 0.5) * 0.3, 0.003, 0.25 + rand() * 0.08);
  return g;
}

function buildShieldSpears() {
  const g = new THREE.Group();
  const wood = mat(0x5a3d25, 0.8);
  const bronze = mat(0xb08d3c, 0.35, 0.8);
  add(g, box(0.6, 0.05, 0.3), wood, 0, 0.025, 0);
  for (const [x, rz] of [[-0.12, 0.18], [0.12, -0.18]] as const) {
    const spear = new THREE.Group();
    add(spear, cyl(0.012, 0.014, 1.6, 8), wood, 0, 0.8, 0);
    add(spear, new THREE.ConeGeometry(0.03, 0.15, 4), mat(0xbfc3c7, 0.3, 0.9), 0, 1.66, 0);
    spear.position.set(x, 0.03, -0.05);
    spear.rotation.z = rz;
    g.add(spear);
  }
  const shield = new THREE.Group();
  add(shield, cyl(0.3, 0.3, 0.03, 32), mat(0x8b1e1e, 0.6)).rotation.x = Math.PI / 2;
  add(shield, new THREE.TorusGeometry(0.3, 0.015, 8, 40), bronze);
  add(shield, sph(0.07, 16, 10), bronze, 0, 0, 0.03).scale.set(1, 1, 0.5);
  shield.position.set(0, 0.36, 0.08);
  shield.rotation.x = -0.18;
  g.add(shield);
  return g;
}

function buildLectern() {
  const g = new THREE.Group();
  const wood = mat(0x5a3a22, 0.65);
  add(g, box(0.4, 0.04, 0.35), wood, 0, 0.02, 0);
  add(g, box(0.1, 1.0, 0.1), wood, 0, 0.54, 0);
  const top = new THREE.Group();
  top.position.set(0, 1.1, 0);
  top.rotation.x = -0.45;
  add(top, box(0.5, 0.03, 0.38), wood);
  add(top, box(0.5, 0.03, 0.02), wood, 0, 0.02, 0.19);
  const map = parchment('parchment');
  const paper = map ? mat(0xffffff, 0.9, 0, { map }) : mat(0xe8d6aa, 0.9);
  for (const side of [-1, 1]) {
    const page = add(top, box(0.2, 0.012, 0.28), paper, side * 0.105, 0.024, 0);
    page.rotation.z = side * -0.08;
  }
  g.add(top);
  return g;
}

function buildAquarium(rand: Rand) {
  const g = new THREE.Group();
  const wood = mat(0x3f2a1c, 0.7);
  add(g, box(0.7, 0.6, 0.36), wood, 0, 0.3, 0);
  add(g, box(0.66, 0.4, 0.32), glass(0xe8f8ff), 0, 0.81, 0);
  add(g, box(0.64, 0.33, 0.3), mat(0x3fa7d6, 0.1, 0, { transparent: true, opacity: 0.35, depthWrite: false }), 0, 0.785, 0);
  add(g, box(0.64, 0.04, 0.3), mat(0xc9b28a, 1), 0, 0.63, 0);
  for (let i = 0; i < 5; i += 1) {
    const weed = add(g, new THREE.ConeGeometry(0.02, 0.14 + rand() * 0.12, 6), mat(0x3f8f4a, 0.6), -0.28 + rand() * 0.56, 0.72, -0.1 + rand() * 0.15);
    weed.rotation.z = (rand() - 0.5) * 0.3;
  }
  const fishColours = [0xff8c2a, 0xffd23f, 0x2a9df4];
  for (let i = 0; i < 3; i += 1) {
    const fish = new THREE.Group();
    add(fish, sph(0.03, 12, 8), mat(fishColours[i], 0.4)).scale.set(1.6, 1, 0.6);
    add(fish, new THREE.ConeGeometry(0.02, 0.035, 4), mat(fishColours[i], 0.4), -0.055, 0, 0).rotation.z = Math.PI / 2;
    fish.position.set(-0.2 + rand() * 0.4, 0.72 + rand() * 0.2, -0.08 + rand() * 0.16);
    fish.rotation.y = rand() * Math.PI;
    g.add(fish);
  }
  add(g, box(0.7, 0.03, 0.36), mat(0x2b2b2b, 0.5), 0, 1.025, 0);
  return g;
}

function buildLabCabinet(rand: Rand) {
  const g = new THREE.Group();
  const white = mat(0xeef0f2, 0.45);
  const W = 0.8;
  const H = 1.7;
  const D = 0.35;
  add(g, box(0.03, H, D), white, -W / 2, H / 2, 0);
  add(g, box(0.03, H, D), white, W / 2, H / 2, 0);
  add(g, box(W, H, 0.015), white, 0, H / 2, -D / 2);
  add(g, box(W, 0.6, D), white, 0, 0.3, 0);
  const liquids = [0x3fb6e8, 0xe84a8f, 0x7ad35a, 0xf2c14e, 0x9b6bd6, 0xff8c42, 0xa0522d];
  for (let s = 0; s < 3; s += 1) {
    const y = 0.62 + s * 0.36;
    add(g, box(W, 0.02, D), white, 0, y, 0);
    let x = -W / 2 + 0.07;
    while (x < W / 2 - 0.07) {
      const h = 0.1 + rand() * 0.14;
      const r = 0.025 + rand() * 0.02;
      add(g, cyl(r, r, h, 12), mat(liquids[Math.floor(rand() * liquids.length)], 0.2, 0, { transparent: true, opacity: 0.8 }), x, y + 0.01 + h / 2, (rand() - 0.5) * 0.12);
      add(g, cyl(r * 0.5, r * 0.5, 0.03, 10), mat(0x222222, 0.5), x, y + 0.025 + h, 0);
      x += r * 2 + 0.03 + rand() * 0.03;
    }
  }
  add(g, box(W - 0.02, H - 0.62, 0.01), glass(0xeaf6ff), 0, 0.6 + (H - 0.6) / 2, D / 2);
  return g;
}

function buildSolidsPlinth(rand: Rand) {
  const g = new THREE.Group();
  add(g, box(0.55, 0.7, 0.45), mat(0xe8e2d6, 0.6), 0, 0.35, 0);
  const shapes: [THREE.BufferGeometry, number][] = [
    [box(0.16, 0.16, 0.16), 0.08],
    [new THREE.ConeGeometry(0.1, 0.24, 32), 0.12],
    [new THREE.IcosahedronGeometry(0.1, 0), 0.1],
    [new THREE.ConeGeometry(0.12, 0.2, 4), 0.1],
  ];
  const colours = [0x2a9df4, 0xe84a5f, 0xf7b32b, 0x43aa8b];
  shapes.forEach(([geo, half], i) => {
    const m = add(g, geo, mat(colours[(i + Math.floor(rand() * 4)) % 4], 0.4, 0, { flatShading: i !== 1 }), ((i % 2) - 0.5) * 0.25, 0.7 + half, (i < 2 ? -1 : 1) * 0.1);
    m.rotation.y = rand() * Math.PI;
  });
  return g;
}

// ---------------------------------------------------------------------------
// More small pieces
// ---------------------------------------------------------------------------

function buildFossil() {
  const g = new THREE.Group();
  add(g, cyl(0.1, 0.11, 0.03, 20), mat(0xb8a888, 0.95, 0, { flatShading: true }), 0, 0.015, 0);
  // An ammonite: a tube along a widening spiral.
  const spiral = Array.from({ length: 60 }, (_, i) => {
    const t = i / 59;
    const a = t * Math.PI * 5;
    const r = 0.012 + t * 0.06;
    return new THREE.Vector3(Math.cos(a) * r, 0, Math.sin(a) * r);
  });
  const shell = add(g, new THREE.TubeGeometry(new THREE.CatmullRomCurve3(spiral), 80, 0.012, 8, false), mat(0x8a7456, 0.8), 0, 0.035, 0);
  shell.scale.set(1, 0.6, 1);
  return g;
}

function buildHourglass() {
  const g = new THREE.Group();
  const wood = mat(0x6b4a30, 0.6);
  add(g, cyl(0.055, 0.055, 0.015, 20), wood, 0, 0.0075, 0);
  add(g, cyl(0.055, 0.055, 0.015, 20), wood, 0, 0.2, 0);
  for (const x of [-0.045, 0.045]) add(g, cyl(0.005, 0.005, 0.18, 6), wood, x, 0.1, 0);
  add(g, lathe([[0.04, 0.015], [0.04, 0.03], [0.006, 0.1], [0.04, 0.175], [0.04, 0.19]], 20), glass(0xfff8e8));
  add(g, new THREE.ConeGeometry(0.035, 0.05, 20), mat(0xd9b77a, 0.9), 0, 0.04, 0);
  add(g, new THREE.ConeGeometry(0.022, 0.035, 20), mat(0xd9b77a, 0.9), 0, 0.14, 0).rotation.x = Math.PI;
  return g;
}

function buildOpenBook(rand: Rand) {
  const g = new THREE.Group();
  const cover = mat(BOOK_COLOURS[Math.floor(rand() * BOOK_COLOURS.length)], 0.7);
  const map = parchment('parchment');
  const paper = map ? mat(0xffffff, 0.9, 0, { map }) : mat(0xf2ecd9, 0.9);
  for (const side of [-1, 1]) {
    const half = new THREE.Group();
    add(half, box(0.14, 0.006, 0.2), cover, side * 0.07, 0.003, 0);
    add(half, box(0.13, 0.014, 0.19), paper, side * 0.07, 0.013, 0);
    half.rotation.z = side * -0.08;
    g.add(half);
  }
  return g;
}

function buildTypewriter() {
  const g = new THREE.Group();
  const body = mat(0x2f4f3f, 0.4, 0.3);
  add(g, box(0.26, 0.06, 0.2), body, 0, 0.03, 0);
  const slope = add(g, box(0.26, 0.05, 0.12), body, 0, 0.07, -0.03);
  slope.rotation.x = 0.3;
  const keys = new THREE.InstancedMesh(cyl(0.008, 0.008, 0.01, 8), mat(0xf2f2ee, 0.5), 30);
  const m = new THREE.Matrix4();
  let n = 0;
  for (let row = 0; row < 3; row += 1) {
    for (let col = 0; col < 10; col += 1) {
      keys.setMatrixAt(n, m.makeTranslation(-0.1 + col * 0.022 + row * 0.006, 0.066 + row * 0.012, 0.07 - row * 0.022));
      n += 1;
    }
  }
  g.add(keys);
  const roller = add(g, cyl(0.02, 0.02, 0.3, 16), mat(0x1a1a1a, 0.5), 0, 0.11, -0.08);
  roller.rotation.z = Math.PI / 2;
  const sheet = add(g, new THREE.PlaneGeometry(0.16, 0.14), mat(0xfdfcf6, 0.9, 0, { side: THREE.DoubleSide }), 0, 0.18, -0.09);
  sheet.rotation.x = -0.2;
  return g;
}

function buildSeedlings(rand: Rand) {
  const g = new THREE.Group();
  add(g, box(0.26, 0.04, 0.16), mat(0x2b2b2b, 0.8), 0, 0.02, 0);
  const shape = new THREE.Shape();
  shape.moveTo(0, 0);
  shape.quadraticCurveTo(0.02, 0.015, 0, 0.035);
  shape.quadraticCurveTo(-0.02, 0.015, 0, 0);
  const leafGeo = new THREE.ShapeGeometry(shape, 4);
  const leaf = mat(0x6cbf4a, 0.6, 0, { side: THREE.DoubleSide });
  for (let i = 0; i < 6; i += 1) {
    const x = -0.08 + (i % 3) * 0.08;
    const z = i < 3 ? -0.04 : 0.04;
    add(g, cyl(0.03, 0.025, 0.005, 12), mat(0x3b2a1c, 1), x, 0.042, z);
    const h = 0.02 + rand() * 0.04;
    add(g, cyl(0.002, 0.002, h, 4), mat(0x8fbf5a, 0.6), x, 0.044 + h / 2, z);
    for (const side of [-1, 1]) {
      const l = add(g, leafGeo, leaf, x, 0.044 + h, z);
      l.rotation.set(0, side > 0 ? 0 : Math.PI, side * -0.9);
    }
  }
  return g;
}

function buildBatteryBulb() {
  const g = new THREE.Group();
  const battery = new THREE.Group();
  add(battery, cyl(0.022, 0.022, 0.09, 16), mat(0x1f3f6e, 0.4, 0.3), 0, 0.045, 0);
  add(battery, cyl(0.022, 0.022, 0.03, 16), mat(0xc87533, 0.3, 0.8), 0, 0.075, 0);
  add(battery, cyl(0.007, 0.007, 0.01, 10), mat(0xbfc3c7, 0.3, 0.9), 0, 0.095, 0);
  battery.rotation.z = Math.PI / 2;
  battery.position.set(-0.07, 0.022, 0);
  g.add(battery);
  add(g, box(0.06, 0.02, 0.06), mat(0x6b4a30, 0.7), 0.08, 0.01, 0);
  add(g, cyl(0.012, 0.012, 0.02, 12), mat(0xbfc3c7, 0.3, 0.9), 0.08, 0.03, 0);
  add(g, sph(0.022, 16, 12), glow(0xfff2a8), 0.08, 0.06, 0);
  const wire = mat(0xd0342c, 0.6);
  for (const z of [-0.012, 0.012]) {
    const curve = new THREE.QuadraticBezierCurve3(
      new THREE.Vector3(-0.02, 0.022, z),
      new THREE.Vector3(0.02, 0.06, z * 4),
      new THREE.Vector3(0.07, 0.03, z)
    );
    add(g, new THREE.TubeGeometry(curve, 16, 0.002, 5, false), z > 0 ? wire : mat(0x222222, 0.6));
  }
  return g;
}

function buildTangram(rand: Rand) {
  const g = new THREE.Group();
  const colours = [0xe84a5f, 0x2a9df4, 0xf7b32b, 0x43aa8b, 0x8e6bd6, 0xff8c42, 0x5bc0be];
  const S = 0.16;
  // The seven tans of a square, as polygons in the unit square.
  const pieces: [number, number][][] = [
    [[0, 0], [1, 0], [0.5, 0.5]],
    [[0, 0], [0.5, 0.5], [0, 1]],
    [[1, 0], [1, 0.5], [0.75, 0.25]],
    [[0.75, 0.25], [1, 0.5], [0.75, 0.75], [0.5, 0.5]],
    [[0.5, 0.5], [0.75, 0.75], [0.25, 0.75]],
    [[0, 1], [0.25, 0.75], [0.75, 0.75], [0.5, 1]],
    [[0.5, 1], [1, 1], [1, 0.5]],
  ];
  pieces.forEach((pts, i) => {
    const shape = new THREE.Shape(pts.map(([x, y]) => new THREE.Vector2((x - 0.5) * S, (y - 0.5) * S)));
    const m = add(g, new THREE.ExtrudeGeometry(shape, { depth: 0.008, bevelEnabled: false }), mat(colours[i], 0.5), 0, 0, 0);
    m.rotation.x = -Math.PI / 2;
    // Slightly apart, as if a student is mid-puzzle.
    m.position.set((rand() - 0.5) * 0.02, 0, (rand() - 0.5) * 0.02);
  });
  return g;
}

function diceFace(n: number) {
  return canvasTex(`dice-${n}`, 64, 64, (ctx, w, h) => {
    ctx.fillStyle = '#f7f7f2';
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = '#1a1a1a';
    const at: Record<number, [number, number][]> = {
      1: [[0.5, 0.5]],
      2: [[0.25, 0.25], [0.75, 0.75]],
      3: [[0.25, 0.25], [0.5, 0.5], [0.75, 0.75]],
      4: [[0.25, 0.25], [0.75, 0.25], [0.25, 0.75], [0.75, 0.75]],
      5: [[0.25, 0.25], [0.75, 0.25], [0.5, 0.5], [0.25, 0.75], [0.75, 0.75]],
      6: [[0.25, 0.22], [0.75, 0.22], [0.25, 0.5], [0.75, 0.5], [0.25, 0.78], [0.75, 0.78]],
    };
    for (const [x, y] of at[n]) {
      ctx.beginPath();
      ctx.arc(x * w, y * h, w * 0.09, 0, Math.PI * 2);
      ctx.fill();
    }
  });
}

function buildDice(rand: Rand) {
  const g = new THREE.Group();
  const faces = [1, 6, 2, 5, 3, 4].map((n) => {
    const map = diceFace(n);
    return map ? mat(0xffffff, 0.4, 0, { map }) : mat(0xf7f7f2, 0.4);
  });
  const geo = box(0.035, 0.035, 0.035);
  for (let i = 0; i < 3; i += 1) {
    const d = add(g, geo, faces, (i - 1) * 0.05 + (rand() - 0.5) * 0.01, 0.0175, (rand() - 0.5) * 0.04);
    d.rotation.y = rand() * Math.PI;
  }
  return g;
}

// ---------------------------------------------------------------------------
// Assembly
// ---------------------------------------------------------------------------

/** Shift a piece so its lowest point is exactly on the floor (or table top). */
function grounded(piece: THREE.Group): THREE.Group {
  piece.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(piece);
  if (Number.isFinite(box.min.y) && Math.abs(box.min.y) > 1e-4) {
    for (const child of piece.children) child.position.y -= box.min.y;
  }
  return piece;
}

export function buildFloorProp(kind: FloorKind, variant: number, band: GradeBand, easel?: EaselBoard): THREE.Group {
  return grounded(buildFloorPiece(kind, variant, band, easel));
}

function buildFloorPiece(kind: FloorKind, variant: number, band: GradeBand, easel?: EaselBoard): THREE.Group {
  const rand = seededRandom(variant + 17);
  switch (kind) {
    case 'rocks':
      return buildRocks(rand);
    case 'globe':
      return buildGlobe();
    case 'strata':
      return buildStrata(rand);
    case 'amphora':
      return buildAmphora(rand);
    case 'column':
      return buildColumn(rand);
    case 'bookshelf':
      return buildBookshelf(rand);
    case 'plant':
      return buildPlant(rand);
    case 'easel':
      return buildEasel(band === 'young' && easel === 'periodic' ? 'chalk' : easel ?? 'chalk');
    case 'flag':
      return buildFlag();
    case 'pendulum':
      return buildPendulum();
    case 'robot':
      return buildRobot(rand);
    case 'bigBlocks':
      return buildBlocks(0.16, 5, rand, 'ABC123');
    case 'volcano':
      return buildVolcano(rand);
    case 'weatherStation':
      return buildWeatherStation();
    case 'tree':
      return buildTree(rand);
    case 'telescope':
      return buildTelescope();
    case 'treasureChest':
      return buildTreasureChest(rand);
    case 'shieldSpears':
      return buildShieldSpears();
    case 'lectern':
      return buildLectern();
    case 'aquarium':
      return buildAquarium(rand);
    case 'labCabinet':
      return buildLabCabinet(rand);
    case 'solidsPlinth':
      return buildSolidsPlinth(rand);
  }
}

export function buildSmallProp(kind: SmallKind, variant: number): THREE.Group {
  return grounded(buildSmallPiece(kind, variant));
}

function buildSmallPiece(kind: SmallKind, variant: number): THREE.Group {
  const rand = seededRandom(variant + 29);
  switch (kind) {
    case 'crystals': return buildCrystals(rand);
    case 'compass': return buildCompass();
    case 'scrolls': return buildScrolls(rand);
    case 'oilLamp': return buildOilLamp();
    case 'coins': return buildCoins(rand);
    case 'sundial': return buildSundial();
    case 'bookStack': return buildBookStack(rand);
    case 'inkwell': return buildInkwell();
    case 'readingLamp': return buildReadingLamp();
    case 'microscope': return buildMicroscope();
    case 'specimenJars': return buildSpecimenJars(rand);
    case 'dnaModel': return buildDna();
    case 'flasks': return buildFlasks(rand);
    case 'tubeRack': return buildTubeRack(rand);
    case 'molecule': return buildMolecule(rand);
    case 'prism': return buildPrism();
    case 'newtonsCradle': return buildNewtonsCradle();
    case 'magnets': return buildMagnets();
    case 'solids': return buildSolids(rand);
    case 'abacus': return buildAbacus(rand);
    case 'setSquares': return buildSetSquares();
    case 'magnifier': return buildMagnifier();
    case 'wateringCan': return buildWateringCan();
    case 'monitor': return buildMonitor();
    case 'circuitBoard': return buildCircuitBoard(rand);
    case 'ballotBox': return buildBallotBox();
    case 'letterBlocks': return buildBlocks(0.05, 4, rand, 'ABCabc');
    case 'fossil': return buildFossil();
    case 'hourglass': return buildHourglass();
    case 'openBook': return buildOpenBook(rand);
    case 'typewriter': return buildTypewriter();
    case 'seedlings': return buildSeedlings(rand);
    case 'batteryBulb': return buildBatteryBulb();
    case 'tangram': return buildTangram(rand);
    case 'dice': return buildDice(rand);
  }
}

/** A side table in the desk's finish, with its small pieces spread along it. */
export function buildSideTable(items: { kind: SmallKind; variant: number }[], finish: DeskFinish): THREE.Group {
  const g = buildDesk({ width: SIDE_TABLE.width, depth: SIDE_TABLE.depth, deskY: SIDE_TABLE.height, groundY: 0, finish });
  g.position.y = SIDE_TABLE.height;
  const outer = new THREE.Group();
  outer.add(g);
  const step = SIDE_TABLE.width / (items.length + 1);
  items.forEach((item, i) => {
    const piece = buildSmallProp(item.kind, item.variant);
    piece.position.set(-SIDE_TABLE.width / 2 + step * (i + 1), SIDE_TABLE.height, (i % 2 ? -1 : 1) * 0.04);
    piece.rotation.y = ((item.variant % 60) - 30) * (Math.PI / 180);
    outer.add(piece);
  });
  return outer;
}

/** Remove and free the room's props. Shared materials and textures stay cached. */
export function disposeRoomProps(scene: THREE.Scene): void {
  const existing = scene.getObjectByName('roomProps');
  if (!existing) return;
  scene.remove(existing);
  existing.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (mesh.isMesh) mesh.geometry.dispose();
  });
}

/** Everything around the desk, as planned by roomTheme.planProps. */
export function placeRoomProps(scene: THREE.Scene, plan: RoomPlan | null, groundY: number, anchorPosition: THREE.Vector3): void {
  disposeRoomProps(scene);
  if (!plan || plan.props.length === 0) return;
  const room = new THREE.Group();
  room.name = 'roomProps';
  room.userData.layer = 'room-props';
  for (const prop of plan.props) {
    const piece = buildPlaced(prop, plan.theme.band, plan.theme.finish);
    piece.position.set(prop.x, groundY, prop.z);
    piece.rotation.y = prop.yaw;
    const near = Math.hypot(prop.x - anchorPosition.x, prop.z - anchorPosition.z) < 2;
    piece.traverse((node) => {
      const mesh = node as THREE.Mesh;
      if (!mesh.isMesh || mesh.userData.contactShadow) return;
      // Only nearby pieces cast into the shadow map; the rest rely on their contact shadow.
      mesh.castShadow = near;
    });
    piece.add(contactShadow(prop.radius));
    room.add(piece);
  }
  scene.add(room);
}

function buildPlaced(prop: PlacedProp, band: GradeBand, finish: DeskFinish): THREE.Group {
  if (prop.type === 'table') return buildSideTable(prop.items, finish);
  const piece = buildFloorProp(prop.kind, prop.variant, band, prop.easel);
  piece.scale.setScalar(prop.scale);
  return piece;
}
